import { motion } from 'framer-motion';

/**
 * Pulsing NyayAssist logo with concentric expanding rings behind it.
 *
 * SOURCE: frontend-prod/features/research/components/ResearchThinkingLoader.tsx:63-94
 * Two ring waves (`bg-background-soft-blue` then `/60`) expand+fade out, while
 * the central logo circle does a subtle scale + bob. Web uses a soft-blue
 * palette; the plugin keeps the same with the primary/20 token so it picks up
 * theme switches automatically.
 */
export interface PulsingLogoProps {
  /** Diameter in pixels. Default 32 fits the 360px pane comfortably. */
  size?: number;
  className?: string;
}

export function PulsingLogo({ size = 32, className }: PulsingLogoProps): JSX.Element {
  const inner = Math.round(size * 0.56);
  return (
    <div
      className={`relative flex items-center justify-center shrink-0 ${className ?? ''}`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <motion.span
        className="absolute inset-0 rounded-full bg-primary/15"
        animate={{ scale: [1, 1.7, 1], opacity: [0.6, 0, 0.6] }}
        transition={{ repeat: Infinity, duration: 2, ease: 'easeOut' }}
      />
      <motion.span
        className="absolute inset-0 rounded-full bg-primary/10"
        animate={{ scale: [1, 1.35, 1], opacity: [0.5, 0, 0.5] }}
        transition={{ repeat: Infinity, duration: 2, ease: 'easeOut', delay: 0.6 }}
      />
      <motion.div
        className="relative z-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-primary/30"
        style={{ width: size, height: size }}
        animate={{ scale: [1, 1.08, 1], y: [0, -1, 0] }}
        transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
      >
        <img
          src="/logo_icon.png"
          alt="NyayAssist"
          width={inner}
          height={inner}
          style={{ width: inner, height: inner, objectFit: 'contain' }}
        />
      </motion.div>
    </div>
  );
}
